import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    AccessSegment,
    ColumnMetadata,
    ColumnTypes,
} from "~/components/DataGrid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import {
    TokenRowData,
    AssetTableRowData,
} from "~/modules/assets/components/AssetTable/types";
import { Anchor } from "~/components/Anchor";
import { RealEstateUserEvents } from "~/modules/analytics/events/real_estate";
import { Emdash } from "~/components/Punctuation";
import { ImageLoader } from "~/components/ImageLoader";
import { cn } from "~/lib/utils";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { Link } from "~/components/Link";
import { getAssetURL } from "~/utils/getAssetURL";
import { TokenNetworkLink } from "./TokenNetworkLink";
import { RWA_COIN_ICON } from "~/consts";

// // // //

const GoogleMapsIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 92.3 132.3"
        className="w-4 h-4 mr-1 shrink-0"
        aria-hidden="true"
        focusable="false"
    >
        <path
            fill="#1a73e8"
            d="M60.2 2.2C55.8.8 51 0 46.1 0 32 0 19.3 6.4 10.8 16.5l21.8 18.3L60.2 2.2z"
        />
        <path
            fill="#ea4335"
            d="M10.8 16.5C4.1 24.5 0 34.9 0 46.1c0 8.7 1.7 15.7 4.6 22l28-33.3-21.8-18.3z"
        />
        <path
            fill="#4285f4"
            d="M46.2 28.5c9.8 0 17.7 7.9 17.7 17.7 0 4.3-1.6 8.3-4.2 11.4 0 0 13.9-16.6 27.5-32.7-5.6-10.8-15.3-19-27-22.7L32.6 34.8c3.3-3.8 8.1-6.3 13.6-6.3"
        />
        <path
            fill="#fbbc04"
            d="M46.2 63.8c-9.8 0-17.7-7.9-17.7-17.7 0-4.3 1.5-8.3 4.1-11.3l-28 33.3c4.8 10.6 12.8 19.2 21 29.9l34.1-40.5c-3.3 3.9-8.1 6.3-13.5 6.3"
        />
        <path
            fill="#34a853"
            d="M59.1 109.2c15.4-24.1 33.3-35 33.3-63 0-7.7-1.9-14.9-5.2-21.3L25.6 98c2.6 3.4 5.3 7.3 7.9 11.3 9.4 14.5 6.8 23.1 12.8 23.1s3.4-8.7 12.8-23.2"
        />
    </svg>
);

export const REAL_ESTATE_LOCATION_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.realEstateCountryName,
    label: (
        <>
            <p>Country</p>
            <p className="text-xs">City</p>
        </>
    ),
    sortsOn: AssetColumns.realEstateCountryName,
    type: ColumnTypes.string,
    width: 215,
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const { real_estate_property } = asset;

            if (!real_estate_property) {
                return <Emdash />;
            }

            const { city, country } = real_estate_property;

            if (!city && !country?.name) {
                return <Emdash />;
            }

            return (
                <div className="flex flex-col">
                    <div className="flex items-center">
                        {country?.icon_url && (
                            <ImageLoader
                                src={country.icon_url}
                                alt={`${country.name} Flag`}
                                className="w-4 rounded-full mr-1.5 shadow-sm"
                                fallbackSrc={RWA_COIN_ICON}
                                fallbackClassName="bg-white opacity-50"
                            />
                        )}
                        <p className="dg-overflow">
                            {country?.name || <Emdash />}
                        </p>
                    </div>
                    <p className="dg-overflow text-sm text-secondary-foreground">
                        {city || <Emdash />}
                    </p>
                </div>
            );
        }

        return <Emdash />;
    },
};

export const REAL_ESTATE_COUNTRY_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.realEstateCountryName,
    label: "Country",
    sortsOn: AssetColumns.realEstateCountryName,
    type: ColumnTypes.string,
    width: 180,
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const { real_estate_property } = asset;

            if (!real_estate_property) {
                return <Emdash />;
            }

            const { country } = real_estate_property;

            if (!country?.name) {
                return <Emdash />;
            }

            return (
                <div className="flex items-center">
                    {country?.icon_url && (
                        <ImageLoader
                            src={country.icon_url}
                            alt={`${country.name} Flag`}
                            className="w-5 rounded-xs mr-1.5 shadow-sm"
                        />
                    )}
                    <p className="dg-overflow">{country.name}</p>
                </div>
            );
        }

        return <Emdash />;
    },
};

export const REAL_ESTATE_CITY_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.realEstateCountryName,
    label: "City",
    sortsOn: AssetColumns.realEstateCountryName,
    type: ColumnTypes.string,
    width: 150,
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const { real_estate_property } = asset;

            if (!real_estate_property) {
                return <Emdash />;
            }

            const { city } = real_estate_property;

            if (!city) {
                return <Emdash />;
            }

            return <p className="dg-overflow">{city}</p>;
        }

        return <Emdash />;
    },
};

export const REAL_ESTATE_ADDRESS_COLUMN_INLINE: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.realEstateStreetAddress,
    label: "Address",
    sortsOn: AssetColumns.realEstateStreetAddress,
    type: ColumnTypes.string,
    width: 250,
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            const { real_estate_property } = asset;

            if (!real_estate_property) {
                return <Emdash />;
            }

            const { street_address, city, state, zip_code, country } =
                real_estate_property;

            if (!street_address?.trim()) {
                return <Emdash />;
            }

            const addressParts = [street_address, city, state, zip_code].filter(
                (part): part is string => Boolean(part?.trim()),
            );

            if (addressParts.length === 0) {
                return <Emdash />;
            }

            const displayAddress = addressParts.join(", ");
            const fullAddressParts = [
                street_address,
                city,
                state,
                zip_code,
                country?.name,
            ].filter((part): part is string => Boolean(part?.trim()));
            const fullAddress = fullAddressParts.join(", ");
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

            return (
                <Anchor
                    href={mapsUrl}
                    openInNewTab
                    analyticsLabel={RealEstateUserEvents.ClickGoogleMapsLink}
                    ariaLabel={`View ${fullAddress} in Google Maps`}
                    variant="default"
                    className="flex items-center gap-1 hover:underline"
                >
                    <GoogleMapsIcon />
                    <span className="dg-overflow">{displayAddress}</span>
                </Anchor>
            );
        }

        return <Emdash />;
    },
};

export const REAL_ESTATE_PROPERTY_TYPE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.realEstatePropertyType,
    label: (
        <>
            Property
            <br />
            Type
        </>
    ),
    sortsOn: AssetColumns.realEstatePropertyType,
    type: ColumnTypes.string,
    width: 120,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;

            const { real_estate_property } = asset;

            if (!real_estate_property || !real_estate_property.property_type) {
                return <Emdash />;
            }

            const { property_type } = real_estate_property;

            if (!property_type || !property_type?.name) {
                return <Emdash />;
            }

            const { name, icon_url } = property_type;

            return (
                <div className="flex items-center">
                    {icon_url && (
                        <ImageLoader
                            src={icon_url}
                            alt={`${name} Flag`}
                            className="w-5 rounded-xs mr-1.5 shadow-sm"
                        />
                    )}
                    <p className="dg-overflow">{name}</p>
                </div>
            );
        }

        return <Emdash />;
    },
};

export const REAL_ESTATE_STRATEGY_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.investmentStrategyType,
    label: (
        <>
            Strategy
            <br />
            Type
        </>
    ),
    sortsOn: AssetColumns.investmentStrategyType,
    type: ColumnTypes.string,
    width: 120,
    cellValue: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            return asset.investment_strategy_type ?? null;
        }
        return null;
    },
};

export const REAL_ESTATE_ASSET_TYPE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: AssetColumns.hasSingleHolding,
    label: (
        <>
            Asset
            <br />
            Type
        </>
    ),
    sortsOn: AssetColumns.hasSingleHolding,
    type: ColumnTypes.string,
    width: 115,
    render: (item) => {
        if (item.isAsset) {
            const asset = item as Asset;
            if (
                asset.asset_pool_id == null ||
                asset.has_single_holding === true
            ) {
                return "Single Asset";
            }
            if (
                asset.has_single_holding === false ||
                (asset.asset_pool_id != null &&
                    asset.has_single_holding == null)
            ) {
                return "Portfolio";
            }
            return <Emdash />;
        }
        return <Emdash />;
    },
};

const GoogleMapsLink = ({ asset }: { asset: Asset }) => {
    const { real_estate_property } = asset;

    if (!real_estate_property) {
        return null;
    }

    const { street_address, city, state, zip_code, country } =
        real_estate_property;

    if (!street_address?.trim()) {
        return null;
    }

    const addressParts = [
        street_address,
        city,
        state,
        zip_code,
        country?.name,
    ].filter((part): part is string => Boolean(part?.trim()));

    if (addressParts.length === 0) {
        return null;
    }

    const address = addressParts.join(", ");
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

    return (
        <Anchor
            href={mapsUrl}
            openInNewTab
            analyticsLabel={RealEstateUserEvents.ClickGoogleMapsLink}
            ariaLabel={`View ${address} in Google Maps`}
            className="flex items-center ml-auto shrink-0 px-1.5 py-0.5 border border-slate-400/20 rounded bg-slate-100/50 hover:bg-slate-200/50 transition-colors"
        >
            <GoogleMapsIcon />
            <span className="text-xs text-muted-foreground">↗</span>
        </Anchor>
    );
};

export const REAL_ESTATE_NAME_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Name",
    slug: AssetColumns.name,
    sortsOn: AssetColumns.name,
    type: ColumnTypes.string,
    width: 300,
    sticky: {
        left: 0,
        isLast: true,
    },
    render: (item) => {
        if (item.isAsset) {
            const { name } = item;

            // NOTE - we include h-11 here so column heights don't "jump" when using the ColumnSwitcher component
            return (
                <div className="flex items-center justify-between select-none gap-2 h-11">
                    <button
                        className={cn(
                            "mr-2 hover:bg-slate-200 transition-colors rounded-sm p-1",
                            {
                                "invisible pointer-events-none":
                                    item.tokens.length < 2,
                            },
                        )}
                        onClick={() => {
                            if (!item.onExpand) {
                                return;
                            }
                            item.onExpand();
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            className={cn(
                                "float-left w-3 h-3 transition-transform",
                                {
                                    "rotate-90": item.expanded,
                                },
                            )}
                        />
                    </button>
                    <div className="flex items-start flex-col justify-center overflow-hidden w-full">
                        <Link
                            href={getAssetURL({ asset: item })}
                            variant="default"
                            className="w-full flex items-center gap-1.5"
                            analyticsLabel={AssetUserEvents.ClickIndividualLink}
                        >
                            <span className="dg-overflow">{name}</span>
                        </Link>

                        {item.issuer && (
                            <span className="font-light text-xs mt-0.5 text-muted-foreground dg-overflow">
                                {item.issuer.name}
                            </span>
                        )}
                    </div>
                    <GoogleMapsLink asset={item} />
                </div>
            );
        }

        const token = item as TokenRowData;
        return <TokenNetworkLink token={token} />;
    },
};
